import { getLang } from '@/helpers/getLang'
import { setJwtToken } from '@/helpers/setRequestHeaders'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

export type ExternalLink = {
    title: string
    description: string
    cards: {
        image: string
        link: string
        text: string
    }[],
    enableToView?: boolean
}

type ExternalLinkDto = {
    title: string
    description: string
    image: string[]
    link: string[]
    text: string[]
}

export const ExternalLinksApi = createApi({
    reducerPath: 'ExternalLinksApi',
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.NEXT_PUBLIC_API_ENDPOINT || 'http://megakorm.ru:3005/api',
        prepareHeaders: setJwtToken
    }),
    tagTypes: ['EXTERNAL-LINK'],
    endpoints: builder => ({
        getExternalLink: builder.query<ExternalLink, { id: number }>({
            query: id => ({ url: `/external-links/${id}` }),
            providesTags: ['EXTERNAL-LINK']
        }),
        getAllExternalLinks: builder.query<ExternalLink[], void>({
            query: () => ({url: `/external-links?lang=${getLang()}`}),
            providesTags: ['EXTERNAL-LINK']
        }),
        getAllExternalLinksEn: builder.query<ExternalLink[], void>({
            query: () => ({url: `/external-links?lang=en`}),
            providesTags: ['EXTERNAL-LINK']
        }),
        likeActions: builder.mutation<any, any>({
            query: data => ({ url: `/articles/like/${data.id}`, method: 'POST', body: data.obj })
        }),
        createExternalLink: builder.mutation<any, ExternalLinkDto>({
            query: data => ({ url: `/external-links`, method: 'POST', body: data }),
            invalidatesTags: ['EXTERNAL-LINK']
        }),
        deleteExternalLinksByIds: builder.mutation<any, any>({
            query: data => ({ url: `/external-links/delete-by-ids`, method: 'POST', body: data }),
            invalidatesTags: ['EXTERNAL-LINK']
        }),
        updateExternalLinksRelease: builder.mutation<any, any>({
            query: data => ({ url: `/external-links/${data.id}`, method: 'PATCH', body: data }),
            invalidatesTags: ['EXTERNAL-LINK']
        }),
        updateExternalLinksEn: builder.mutation<any, any>({
            query: data => {
                let id = data.id
                let body = {...data}
                delete body.id
                return { url: `/external-links/en/update/${id}`, method: 'PATCH', body: body }
            },
            invalidatesTags: ["EXTERNAL-LINK"]
        }),
        toggleEnableToViewExternalLink: builder.mutation<any, { id: number }>({
            query: data => ({ url: '/external-links/enable-to-view', method: 'POST', body: data }),
            invalidatesTags: ['EXTERNAL-LINK']
        }),
        toggleEnableToViewByListExternalLink: builder.mutation<any, { ids: number[]; enable?: boolean }>({
            query: data => ({ url: '/external-links/enable-to-view/list', method: 'POST', body: data }),
            invalidatesTags: ['EXTERNAL-LINK']
        })
    })
})

export const {
    useGetExternalLinkQuery,
    useGetAllExternalLinksQuery,
    useGetAllExternalLinksEnQuery,
    useCreateExternalLinkMutation,
    useDeleteExternalLinksByIdsMutation,
    useUpdateExternalLinksReleaseMutation,
    useUpdateExternalLinksEnMutation,
    useLikeActionsMutation,
    useToggleEnableToViewByListExternalLinkMutation,
    useToggleEnableToViewExternalLinkMutation
} = ExternalLinksApi
